import AddMoreButton from "apps/console/components/addMoreButton";
import Icon from "assets/icons";
import classNames from "classnames";
import { AnyObject } from "global";
import React from "react";
import NoDataFound from "shared/components/noDataFound";
import FormContainer from "shared/form";
import { useCopies } from "shared/hooks/useCopies";
import I18n from "shared/types/I18n";

export default function FieldArrayWrapper<T>({
  customAddOptionCallback,
  defaultValuesObj,
  showAddOption = true,
  ...props
}: {
  renderComponent: (renderComponentProps: { index: number; remove: (index: number) => void }) => React.ReactNode;
  name: string;
  values: AnyObject;
  noOptionsLabelKey: keyof I18n;
  noOptionsIcon?: Icon;
  labelsDiv: JSX.Element;
  disabled?: boolean;
  defaultValuesObj?: T;
  customAddOptionCallback?: (insert: (index: number, value: any) => void, fieldArrayValues: Array<T>) => void;
  disableAddOption?: boolean;
  showAddOption?: boolean;
  additionalClass?: string;
  classicStyle?: boolean;
}) {
  const copies = useCopies();
  const addOption = React.useCallback(
    (insert, fieldArrayValues) => {
      if (customAddOptionCallback) {
        customAddOptionCallback(insert, fieldArrayValues);
      } else if (defaultValuesObj) {
        insert(fieldArrayValues.length, { ...defaultValuesObj });
      }
    },
    [customAddOptionCallback, defaultValuesObj]
  );

  return (
    <FormContainer.FieldArray name={props.name}>
      {({ insert, remove }) => {
        return (
          <div
            className={classNames("materialListTable", props.additionalClass, {
              emptyTable: props.values[props.name].length <= 0,
              classicStyledTable: props.classicStyle
            })}>
            {props.values[props.name].length > 0 ? (
              <React.Fragment>
                {props.labelsDiv}
                <ul className="materialListTable_body">
                  {props.values[props.name].map((o: T, idx: number) => (
                    <li key={`${idx}` + o}>{props.renderComponent({ remove, index: idx })}</li>
                  ))}
                </ul>
              </React.Fragment>
            ) : (
              <NoDataFound icon={props.noOptionsIcon} label={copies.get(props.noOptionsLabelKey)} />
            )}
            {showAddOption && (
              <AddMoreButton
                disabled={props.disableAddOption}
                hasSeperator={props.values[props.name].length > 0}
                onClick={() => addOption(insert, props.values[props.name])}
              />
            )}
          </div>
        );
      }}
    </FormContainer.FieldArray>
  );
}
